import { useEffect, useMemo, useState } from "react";
import { Route, Switch, useHistory, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { routes } from "./routes";

import * as actions from "@/redux/actions";

import userClient from "@/apis/userClient";

import SntBrowserTabTitle from "@cleanup/components/SntBrowserTabTitle";
import { useMediaQuery } from "react-responsive";
import { useDebouncedEffect } from "@/hooks/useDebouncedEffect";
import commonClient from "@/apis/commonClient";
import TagManager from "@/gtm/TagManager";
import GoogleTagManager from "@/constants/GoogleTagManager";
import { AppLanguageProvider } from "@/contexts/AppLanguageContext";
import { FavoritesProvider } from "@/contexts/FavoritesContext";
import { RecentsProvider } from "@/contexts/RecentsContext";
import { ToastProvider } from "@/hooks/useToast";
import SntToastContainer from "@cleanup/widgets/SntToastContainer";
import type { RootState } from "@/types/RootState";
import type { RouteConfig } from "@/types/RouteConfig";
import usageAnalyticsClient, { derivePageInfo } from "@/apis/usageAnalyticsClient";

interface GTMResponse {
  settingValue?: string;
  nonce?: string;
}

const App = (): JSX.Element | null => {
  const dispatch = useDispatch();
  const loginInfo = useSelector((state: RootState) => state.user);

  const [languageLoading, setLanguageLoading] = useState(true);
  const [lang] = useState(loginInfo.language || sessionStorage.getItem("SNT-LOCALE") || "");
  const [forceRerender, setForceRerender] = useState(false);

  const location = useLocation();

  const [isGtmReady, setIsGtmReady] = useState(false);
  const [isGetCsrf, setIsGetCsrf] = useState(false);

  const history = useHistory();
  const isTabletOrMobile = useMediaQuery({ query: "(max-width: 767px)" });

  // redirect url from alert email.
  if (history.location.hash.startsWith("#/admin/")) {
    history.push(history.location.hash.replace("#/admin/", ""));
  }

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  // Usage analytics: track page views with friendly tab names
  // Patches pushState + replaceState because tab switching uses replaceState directly,
  // which bypasses React Router's useLocation()
  useEffect(() => {
    let prevUrl = "";

    const trackPageView = () => {
      if (!loginInfo?.userId) return;
      const { url, title } = derivePageInfo();

      // Deduplicate: don't track the same URL twice in a row
      if (url === prevUrl) return;
      prevUrl = url;

      document.title = title;
      usageAnalyticsClient.trackPageView(url, title);
    };

    // Patch pushState (React Router navigation) and replaceState (tab switching)
    const origPushState = window.history.pushState;
    window.history.pushState = function (
      ...args: Parameters<typeof origPushState>
    ) {
      origPushState.apply(this, args);
      trackPageView();
    };

    const origReplaceState = window.history.replaceState;
    window.history.replaceState = function (
      ...args: Parameters<typeof origReplaceState>
    ) {
      origReplaceState.apply(this, args);
      trackPageView();
    };

    // Back/forward buttons
    window.addEventListener("popstate", trackPageView);

    // Track initial page view
    trackPageView();

    return () => {
      window.history.pushState = origPushState;
      window.history.replaceState = origReplaceState;
      window.removeEventListener("popstate", trackPageView);
    };
  }, [loginInfo?.userId]);

  // Usage analytics: identify user with org and partner info
  useEffect(() => {
    if (!loginInfo?.userId) return;

    usageAnalyticsClient.identify({
      userId: loginInfo.userId,
      // username is intentionally not traced for privacy reasons
      userType: loginInfo.userType,
      orgId: loginInfo.organisation?.id?.toString(),
      orgName: loginInfo.organisation?.name,
      orgType: loginInfo.organisation?.organisationType,
      partnerId: loginInfo.partner?.id != null ? String(loginInfo.partner.id) : undefined,
      partnerName: loginInfo.partner?.name,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loginInfo?.userId]);

  useEffect(() => {
    userClient.getLanguage(lang || undefined).then((data) => {
      setLanguageLoading(false);
      dispatch(actions.updateLanguage(data.data));
    });
  }, [lang, dispatch]);

  useEffect(() => {
    const VITE_GTM_ID = GoogleTagManager.VITE_GTM_ID;
    const NO_VALUE = GoogleTagManager.NO_VALUE;

    const viteGTMId = sessionStorage.getItem(VITE_GTM_ID);
    const hostName = window.location.hostname;

    commonClient
      .getCsrfName()
      .then(({ data }) => {
        sessionStorage.setItem("csrf_name", data);
      })
      .catch(() => {})
      .finally(() => {
        setIsGetCsrf(true);
      });

    if (viteGTMId || hostName === "localhost") {
      setIsGtmReady(true);
    } else {
      commonClient
        .getGTMContainerId()
        .then(({ data }: { data: GTMResponse }) => {
          if (data && data.settingValue) {
            console.log("Initialize VITE_GTM_ID");
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (TagManager as any).initialize({
              gtmId: data.settingValue,
              nonce: data.nonce,
            });
            sessionStorage.setItem(VITE_GTM_ID, data.settingValue);
          } else {
            sessionStorage.setItem(VITE_GTM_ID, NO_VALUE);
          }
        })
        .catch(() => {
          sessionStorage.setItem(VITE_GTM_ID, NO_VALUE);
        })
        .finally(() => {
          setIsGtmReady(true);
        });
    }
  }, []);

  const channel = useMemo(() => {
    return new BroadcastChannel("impersonate_reload");
  }, []);

  useEffect(() => {
    channel.onmessage = () => {
      console.log("reload. impersonate_reload");
      window.location.reload();
    };

    return () => {
      channel.close();
    };
  }, [channel]);

  useEffect(() => {
    history.listen(() => {
      // only happen when navigating between pages (when location.pathname is updated)
      const newestSNTVersion = sessionStorage.getItem("NEWEST-SNT-VERSION");
      const loginSNTVersion = sessionStorage.getItem("SNT-VERSION");
      if (
        newestSNTVersion &&
        loginSNTVersion &&
        newestSNTVersion !== loginSNTVersion
      ) {
        sessionStorage.setItem("SNT-VERSION", newestSNTVersion);

        // override again to prevent http async response issue (or cache if the checking function is not good enough)
        sessionStorage.setItem("NEWEST-SNT-VERSION", newestSNTVersion);

        setForceRerender(true);
      } else if (!loginSNTVersion && newestSNTVersion) {
        sessionStorage.setItem("SNT-VERSION", newestSNTVersion);
      }
    });

    const dataStore = localStorage.getItem("snt_control_layer");
    if (dataStore) {
      dispatch(actions.storeLayer(JSON.parse(dataStore) as Record<string, unknown>));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useDebouncedEffect(
    () => {
      if (forceRerender) {
        console.log(
          "reload after 1s. loginSNTVersion:",
          sessionStorage.getItem("SNT-VERSION")
        );
        window.location.reload();
      }
    },
    1000,
    [forceRerender]
  );

  useEffect(() => {
    dispatch(actions.setIsMobile({ isMobile: isTabletOrMobile }));
  }, [isTabletOrMobile, dispatch]);

  function showRoutes(routes: RouteConfig[]): JSX.Element[] | null {
    let rs: JSX.Element[] | null = null;
    if (routes.length > 0) {
      rs = routes.map((route, idx) => {
        return (
          <Route
            key={idx}
            path={route.path}
            exact={route.exact}
            component={route.component}
          />
        );
      });
    }
    return rs;
  }

  if (languageLoading) {
    return null;
  }

  if (!isGetCsrf || !isGtmReady) {
    return null;
  }

  return (
    <>
      <ToastProvider>
        <AppLanguageProvider>
          <FavoritesProvider>
            <RecentsProvider>
              <SntBrowserTabTitle />
              <Switch>{showRoutes(routes)}</Switch>
              <SntToastContainer />
            </RecentsProvider>
          </FavoritesProvider>
        </AppLanguageProvider>
      </ToastProvider>
    </>
  );
};

export default App;
